import React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';

import Client from './components/Client';
import Config from './components/ClientConfig';
import DirectPromotion from './components/DirectPromotion';
import DirectPromotionsFilters from './components/DirectPromotionsFilters';
import Visibility from './Visibility';

export default class DirectPromotions extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      client: new Client('none'), items: [], addNew: false,
      filterData: { limit: 20 }, isLoading: true, error: '',
    };
    this.restricted = new Visibility('promotions');
    this.requestId = 0;
    this.activeRequest = null;
    this.unmounted = false;
  }

  componentDidMount() {
    this.unmounted = false;
    if (this.restricted.scope.visible) this.getData();
  }

  componentWillUnmount() {
    this.unmounted = true;
    this.requestId += 1;
    this.activeRequest?.abort?.();
  }

  getData = () => this.applyFilters(this.state.filterData);

  applyFilters = async (queryData = {}) => {
    if (!this.restricted.scope.visible || this.unmounted) return;
    const requestId = ++this.requestId;
    this.activeRequest?.abort?.();
    const query = { limit: 20, order: '-dateFrom', ...queryData, with_price_warning: 1 };
    this.setState({ filterData: query, items: [], isLoading: true, error: '' });
    try {
      this.activeRequest = this.state.client.getDataAsync(Config.API_URL_PROMOTION_DIRECT, query);
      const data = await this.activeRequest;
      if (this.unmounted || requestId !== this.requestId) return;
      if (!Array.isArray(data?.directPromotions)) throw new Error('Invalid response');
      this.setState({ items: data.directPromotions });
    } catch (error) {
      if (this.unmounted || requestId !== this.requestId) return;
      const message = error?.status === 401
        ? 'Your session has expired. Sign in again to view promotions.'
        : error?.status === 403
          ? 'You do not have permission to view these promotions.'
          : error?.status === 400
            ? 'The filters could not be applied. Check the filter values and try again.'
            : 'Could not load promotions. Please try again.';
      this.setState({ error: message });
    } finally {
      if (!this.unmounted && requestId === this.requestId) {
        this.activeRequest = null;
        this.setState({ isLoading: false });
      }
    }
  };

  toggleAdd = () => {
    if (!this.restricted.scope.edit) return;
    this.setState((state) => ({ addNew: !state.addNew }));
    window.scrollTo(0, 0);
  };

  itemUpdated = () => {
    this.setState({ addNew: false }, this.getData);
  };

  render() {
    if (!this.restricted.scope.visible) return <div />;
    const { items, isLoading, error } = this.state;
    return (
      <div style={{ backgroundColor: '#273442', minHeight: '100%' }}>
        <div className="sticky">
          <DirectPromotionsFilters
            applyFilters={this.applyFilters}
            toggleAdd={this.toggleAdd}
            isAdding={this.state.addNew}
            itemsCounter={items.length}
            isLoading={isLoading}
            hasError={!!error}
            allowEdit={this.restricted.scope.edit}
          />
        </div>

        {this.state.addNew && (
          <DirectPromotion
            key="new" isNew itemUpdated={this.itemUpdated}
            allowEdit={this.restricted.scope.edit}
          />
        )}

        {isLoading ? (
          <Box sx={{ p: 2 }} aria-label="Loading promotions">
            <Skeleton variant="rounded" width="100%" height={180} />
            <Skeleton variant="rounded" width="100%" height={180} sx={{ mt: 1 }} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ m: 1 }} action={<Button color="inherit" onClick={this.getData}>Retry</Button>}>
            {error}
          </Alert>
        ) : items.length === 0 ? (
          <Alert severity="info" sx={{ m: 1 }}>No promotions match these filters.</Alert>
        ) : (
          <>
            {items.map((item) => (
              <DirectPromotion
                key={item.iref} item={item} itemUpdated={this.itemUpdated}
                allowEdit={this.restricted.scope.edit}
              />
            ))}
            {items.length === this.state.filterData.limit && (
              <Alert severity="info" sx={{ m: 1 }}>Showing the first {items.length} matches. Refine the filters to narrow the results.</Alert>
            )}
          </>
        )}
      </div>
    );
  }
}
